import React, { useState, useEffect } from 'react';
import { BASE_URL } from '../../apis/api';
import { useNavigate } from 'react-router-dom';
import RouteConstants from '../../constant/Routeconstant';
import Loader from './Loader';
import videoBg from '../../images/cover/video-bg.jpg';
import { CalendarDays, CheckCircle, Clock3, Clock3Icon, Theater } from 'lucide-react';
import venueIcon from '../../images/featuressession/auditorium-icon.png';

const FeaturedSessions = () => {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    // Fetch sessions data
    useEffect(() => {
        const fetchSessions = async () => {
            try {
                const response = await fetch(`${BASE_URL}/session/listfeaturedsession`);
                const result = await response.json();

                if (result.data && Array.isArray(result.data)) {
                    setSessions(result.data);
                } else {
                    setSessions([]);
                }
            } catch (err) {
                console.error('Sessions fetch error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchSessions();
    }, []);

    const getImageUrl = (imagePath) => {
        return imagePath ? `${BASE_URL}/${imagePath}` : '/placeholder-image.jpg';
    };

    const formatTime = (timeString) => {
        if (!timeString) return '';
        const time = new Date(`2000-01-01T${timeString}`);
        return time.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const handleViewAll = () => {
        console.log('Navigate to all sessions');
    };

    if (loading) {
        return <Loader />;
    }

    if (!sessions.length) {
        return (
            <div className="py-16 text-center text-gray-500 bg-gray-50">
                No featured sessions found
            </div>
        );
    }

    return (
        <div
            className="py-16 px-4 bg-gray-50 sessions"
            style={{
                backgroundImage: `url(${videoBg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
            }}
        >
            <div className="max-w-7xl mx-auto sessionsContainer">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="flex items-center justify-center mb-4 sm:mb-6">
                        <div className="h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent flex-1 max-w-16 sm:max-w-24 md:max-w-32"></div>
                        <h4 className="text-xl sm:text-2xl md:text-3xl font-semibold text-gray-800 mx-4 sm:mx-6 md:mx-8 relative">
                            <span className="bg-gradient-to-r from-gray-800 via-gray-700 to-gray-600 bg-clip-text text-black">
                                FEATURED SESSIONS
                            </span>
                            <div className="absolute -bottom-2 sm:-bottom-2 left-1/2 transform -translate-x-1/2 w-10 sm:w-12 md:w-16 h-1 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full"></div>
                        </h4>
                        <div className="h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent flex-1 max-w-16 sm:max-w-24 md:max-w-32"></div>
                    </div>
                    <p className="text-black text-lg max-w-3xl mx-auto">
                        Explore our curated selection of premium sessions
                    </p>
                </div>

                {/* Sessions Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {sessions.map((session) => (
                        <div
                            key={session.id}
                            className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-visible border-2 border-yellow-400 relative sessionCard"
                        >
                            <div className="p-6">
                                {/* Featured Badge */}
                                {session.highlight === 1 && (
                                    <div className="absolute -top-3 left-4 bg-yellow-400 text-black text-xs font-normal px-3 py-1 rounded-full z-10">
                                        Featured
                                    </div>
                                )}

                                {/* Session Title */}
                                <h3 className="text-lg font-bold text-black leading-tigh min-h-[2rem] mt-2">
                                    {session.name}
                                </h3>
                               <p className="flex items-center text-xs mb-2">
                                <img src={venueIcon} className="w-3 h-3 mr-1" alt="Venue Icon" />
                                {session?.venue?.name}
                                </p>


                                {/* Date and Time in same line */}
                                <div className="flex items-center justify-between text-xs text-black mb-4">
                                    <div className="flex items-center">
                                        <CalendarDays className="text-black w-6 h-6 p-1" />
                                        <span>{formatDate(session.date)}</span>
                                    </div>
                                    <div className="flex items-center">
                                        <Clock3Icon className="text-black w-6 h-6 p-1" />
                                        <span>{formatTime(session.time_from)} - {formatTime(session.time_to)}</span>
                                    </div>
                                </div>

                                {/* Language and Category Tags */}
                                <div className="flex flex-wrap gap-2 mb-6">
                                    <span className="px-3 py-1 bg-yellow-100 text-black text-xs font-medium rounded-full">
                                        {session.language}
                                    </span>
                                    {session.category && (
                                        <span className="px-3 py-1 bg-teal-100 text-black text-xs font-medium rounded-full">
                                            {session.category.name}
                                        </span>
                                    )}
                                </div>

                                {/* Speakers Section with centered heading and lines */}
                                {(session.speakers?.length > 0 || session.session_speackers?.length > 0) && (
                                    <div className="mb-4 speakerList">
                                        <div className="flex items-center mb-3">
                                            <div className="flex-1 border-t border-[#dadada]"></div>
                                            <div className="bg-yellow-400 text-black text-xs font-normal px-3 py-1 rounded-full mx-3">
                                                Speakers
                                            </div>
                                            <div className="flex-1 border-t border-[#dadada]"></div>
                                        </div>
                                        <div className="space-y-3">
                                            {/* Main speakers */}
                                            {session.speakers?.slice(0, 2).map((speaker) => (
                                                <div key={speaker.id} className="flex items-center space-x-3">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center space-x-2">
                                                            <CheckCircle className="text-green-500 w-4 h-4" />
                                                            <p className="text-sm font-bold text-black truncate">
                                                                {speaker.heading}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}

                                            {/* Session speakers */}
                                            {session.session_speackers?.slice(0, 2).map((sessionSpeaker) => (
                                                <div key={sessionSpeaker.id} className="flex items-center space-x-3 speakerBlncList">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center space-x-2">
                                                            <CheckCircle className="text-green-500 w-4 h-4" />
                                                            <p className="text-sm font-bold text-black truncate">
                                                                {sessionSpeaker.speaker?.heading}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                           
                                        </div>
                                        {/* Chair section */}
                                        {session?.session_moderators?.length > 0 && (
                                        <div>
                                             <div className="flex items-center my-3">
                                            <div className="flex-1 border-t border-[#dadada]"></div>
                                            <div className="bg-yellow-400 text-black text-xs font-normal px-3 py-1 rounded-full mx-3">
                                                Chair
                                            </div>
                                            <div className="flex-1 border-t border-[#dadada]"></div>
                                        </div>
                                         {session.session_moderators?.slice(0, 2).map((sessionSpeaker) => (
                                                <div key={sessionSpeaker.id} className="flex items-center space-x-3 speakerBlncList">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center space-x-2">
                                                            <CheckCircle className="text-green-500 w-4 h-4" />
                                                            <p className="text-sm font-bold text-black truncate">
                                                                {sessionSpeaker.speaker?.heading}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        )}
                                        <div className="border-t mt-4 border-[#dadada]"></div>
                                    </div>
                                    
                                )}

                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default FeaturedSessions;